// Group doses into stacked-row form for the DoseTimingChart.
//
// Each row is one calendar day in the user's local TZ, with today at
// the top and `daysBack - 1` past rows below it. Within each row, doses
// are sorted by minute-of-day so the chart can plot them as dots
// against a 24h x-axis.
//
// All TZ math goes through shared/doseTimingPattern.ts's `localParts` so
// the chart and the server-side habitual-time analysis agree on which
// day a dose "belongs to" (relevant for late-night doses near
// midnight). Pure function — tested in tests/doseTimingGrouping.test.ts.

import { localParts } from '@shared/doseTimingPattern';
import { ymdAddDays } from './format';

export interface TimingDoseDot {
  /** 0..1439, where 720 = noon in the user's TZ. */
  minuteOfDay: number;
  mg: number;
}

export interface TimingDayRow {
  /** YYYY-MM-DD in the user's TZ. */
  date: string;
  /** 0 = today, -1 = yesterday, etc. */
  dayOffset: number;
  /** Short label for the left margin: "Today", "Yesterday", "3 days ago". */
  label: string;
  doses: TimingDoseDot[];
}

export interface GroupDosesOptions {
  /** IANA timezone the user lives in. */
  tz: string;
  /** Number of rows to produce. Default 3 (today + 2 prior). */
  daysBack?: number;
  /** Reference "now" — injected for tests. */
  now?: Date;
}

export function groupDosesByDayAndMinute(
  doses: ReadonlyArray<{ ts: string; computedMg7oh: number }>,
  opts: GroupDosesOptions,
): TimingDayRow[] {
  const daysBack = Math.max(1, opts.daysBack ?? 3);
  const now = opts.now ?? new Date();
  const todayYmd = localParts(now, opts.tz).ymd;

  const rows: TimingDayRow[] = [];
  for (let off = 0; off < daysBack; off++) {
    const date = ymdAddDays(todayYmd, -off);
    rows.push({
      date,
      dayOffset: -off,
      label: labelForOffset(-off),
      doses: [],
    });
  }

  const rowsByDate = new Map(rows.map((r) => [r.date, r] as const));

  for (const d of doses) {
    if (!Number.isFinite(d.computedMg7oh) || d.computedMg7oh <= 0) continue;
    const t = Date.parse(d.ts);
    if (!Number.isFinite(t)) continue;
    const parts = localParts(new Date(t), opts.tz);
    const row = rowsByDate.get(parts.ymd);
    if (!row) continue;
    row.doses.push({
      minuteOfDay: parts.hour * 60 + parts.minute,
      mg: d.computedMg7oh,
    });
  }

  for (const r of rows) r.doses.sort((a, b) => a.minuteOfDay - b.minuteOfDay);
  return rows;
}

function labelForOffset(offset: number): string {
  if (offset === 0) return 'Today';
  if (offset === -1) return 'Yesterday';
  return `${-offset} days ago`;
}

// Map a dose's mg to a dot radius in px. Square-root scaling so big
// doses read bigger but tiny ones don't disappear into the grid line.
// Clamped so a 100 mg outlier doesn't blow up the visual rhythm.
export function doseDotRadius(mg: number): number {
  const raw = Math.sqrt(Math.max(0, mg)) * 1.1;
  return Math.max(2.5, Math.min(7, raw));
}

// Opacity falls off by row age. Today is solid; older rows fade so the
// eye lands on the current day first.
export function rowOpacityForOffset(offset: number): number {
  const off = Math.abs(offset);
  if (off === 0) return 1;
  return Math.max(0.25, 1 - off * 0.18);
}
